import { useEffect, useMemo } from 'react';

import { ThemeProvider } from 'styled-components';

import { BrowserRouter } from 'react-router-dom';

import { UIGlobalStyleBase } from '@aquariuxfintech/aqx-trader-react-ui/global-style';
import { PortalRoot } from '@aquariuxfintech/aqx-trader-react-ui/portal';
import { aqxTraderSDKConfig } from '@aquariuxfintech/aqx-trader-sdk-config';
import {
  AqxTraderThemeProvider,
  EAqxTraderThemeMode,
  IAqxTraderTheme
} from '@aquariuxfintech/aqx-trader-sdk-config/theme';

import { ELoginSource } from '@aqx-core/common/api/auth';
import cookies, { ECookieKey } from '@aqx-core/common/cookies';
import { useRootConfig } from '@aqx-core/common/hooks/root/use-root-config';
import { useInitColorTheme } from '@aqx-core/common/hooks/theme/use-init-color-theme';
import { appLanguageManager } from '@aqx-core/common/i18n';
import { authenticationViewController } from '@aqx-core/common/services/auth/controller';
import { companyViewController } from '@aqx-core/common/services/company/controller';
import storage, { EStorageKey } from '@aqx-core/common/storage';
import { EAppThemeMode, useAppTheme } from '@aqx-core/common/theme';
import { ibxSansFontFamily } from '@aqx-core/common/theme/font';

import { useInitDatadog } from '../hooks/use-init-datadog';
import { useInitWebAlert } from '../hooks/use-init-web-snackbar';
import { GlobalScrollbarStyles } from '../styles/global-scrollbar';
import Router from './router';

const Screens: FCC = () => {
  useRootConfig();
  const { theme } = useAppTheme();

  // To init snackbar service
  useInitWebAlert();
  useInitColorTheme();
  useInitDatadog();

  useEffect(() => {
    appLanguageManager.init(async () => {
      const language = cookies.get(ECookieKey.LANGUAGE);
      if (language) {
        appLanguageManager.initLanguage(language);
      }
      companyViewController.setLangInitialised(true);
    });
  }, []);

  useEffect(() => {
    authenticationViewController.init(ELoginSource.WEB);
  }, []);

  useEffect(() => {
    aqxTraderSDKConfig.init({
      baseUrl: '',
      socketPathPrefix: `//${location.host}`,
      getToken: () => {
        return storage.getItem(EStorageKey.TOKEN);
      }
    });
  }, []);

  const aqxTraderTheme: IAqxTraderTheme = useMemo(() => {
    return {
      themeMode: theme.themeMode === EAppThemeMode.DARK ? EAqxTraderThemeMode.DARK : EAqxTraderThemeMode.LIGHT,
      themeColors: theme.themeColors,
      fontFamily: theme.fontFamily
    };
  }, [theme]);

  const font = theme.fontFamily?.DEFAULT_FONT_FAMILY ?? ibxSansFontFamily.DEFAULT_FONT_FAMILY;

  return (
    <ThemeProvider theme={theme}>
      {/* Always use createTheme() before passing in  */}
      <AqxTraderThemeProvider theme={aqxTraderTheme}>
        <BrowserRouter>
          <PortalRoot>
            <UIGlobalStyleBase $fontFamily={`${font}, sans-serif`} />
            <GlobalScrollbarStyles />
            <Router />
          </PortalRoot>
        </BrowserRouter>
      </AqxTraderThemeProvider>
    </ThemeProvider>
  );
};

export default Screens;
